import { useState } from "react";
import { toast } from "sonner";
import { Instagram, Copy, RefreshCw, Loader2, Image as ImageIcon } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const copy = (text, what) => navigator.clipboard.writeText(text).then(() => toast.success(`${what} copied`), () => toast.error("Copy failed — select and copy manually"));

const Wrap = ({ bare, children }) => (bare ? (
  <details data-testid="promo-prompts" className="mt-4"><summary className="cursor-pointer text-xs text-stone-500 hover:text-[#2B2A26]">Image prompts</summary>{children}</details>
) : children);

export const PromoCard = ({ kit, reload, bare }) => {
  const [busy, setBusy] = useState(false);
  const p = kit.promo;
  const make = async () => {
    setBusy(true);
    try { await http.post(`/kits/${kit.id}/promo`); toast.success("Instagram caption written"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const tags = (p?.hashtags || []).join(" ");
  return (
    <div data-testid="promo-card" className={bare ? "" : "rounded-xl border border-stone-200 bg-white p-6"}>
      <div className="flex items-center justify-between gap-3">
        <div className="eyebrow flex items-center gap-2"><Instagram size={14} /> Instagram post</div>
        <button data-testid="promo-generate" onClick={make} disabled={busy || !kit.sections?.length} className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-1.5 text-xs hover:bg-stone-100 disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {p ? "Rewrite" : "Write caption"}
        </button>
      </div>
      {!p && <p className="text-sm text-stone-500 mt-3">A promotional caption and hashtags are written automatically when the kit finishes building.</p>}
      {p && <>
        <div data-testid="promo-caption" className={`mt-4 whitespace-pre-line rounded-sm bg-stone-50 p-4 text-sm leading-relaxed text-stone-700 ${bare ? "max-h-52 overflow-y-auto border border-stone-200" : ""}`}>{p.caption}</div>
        <div data-testid="promo-hashtags" className="mt-3 text-xs text-[#9A7B2C] leading-relaxed">{tags}</div>
        {p.first_comment && <div className="mt-3 text-xs text-stone-500"><b>First comment:</b> {p.first_comment}</div>}
        {p.alt_text && <div className="mt-1 text-xs text-stone-500"><b>Alt text:</b> {p.alt_text}</div>}
        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="promo-copy-all" onClick={() => copy(`${p.caption}\n\n${tags}`, "Caption + hashtags")} className="inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white"><Copy size={13} /> Copy caption + hashtags</button>
          <button data-testid="promo-copy-tags" onClick={() => copy(tags, "Hashtags")} className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-1.5 text-xs"><Copy size={13} /> Hashtags only</button>
          {kit.images?.cover && <span className="text-xs text-stone-400 self-center">Use the cover photo from the Photography tab as the image.</span>}
        </div>
        <Wrap bare={bare}>{[["image_prompt", "Image prompt · feed post 4:5"], ["story_image_prompt", "Image prompt · Story / Reel 9:16"]].map(([k, label]) => (
          <div key={k} className="mt-5">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 flex items-center gap-1.5"><ImageIcon size={12} /> {label}</div>
              {p[k] && <button data-testid={`promo-copy-${k.replace(/_/g, "-")}`} onClick={() => copy(p[k], "Image prompt")} className="inline-flex items-center gap-1 rounded-sm border border-stone-300 px-2 py-1 text-[11px]"><Copy size={12} /> Copy</button>}
            </div>
            {p[k] ? <div data-testid={`promo-${k.replace(/_/g, "-")}`} className="mt-1.5 rounded-sm bg-[#FBF6E9] p-3 text-xs leading-relaxed text-stone-700">{p[k]}</div>
              : <div data-testid={`promo-${k.replace(/_/g, "-")}-missing`} className="mt-1.5 text-xs text-stone-400">Not written yet — press Rewrite to add it.</div>}
          </div>
        ))}</Wrap>
      </>}
    </div>
  );
};
